/**
 * A hook that provides the currently selected language, supportedLangugages, and a callback to switch languages.
 *
 * @module
 */
import { getSupoportedLanguages as getSupoportedLanguagesFromWindow, getLanguageId, } from '../utils/globals';
/**
 * Change the currently selected language of a KB
 */
function changeLanguage(languageId) {
    window.location.assign(`/${languageId}`);
}
const languages = [
    {
        id: 'ar',
        label: 'اللغة العربية',
        anglicisedSuffix: '(Arabic)',
        iconPath: getIconPath('ar'),
    },
    {
        id: 'bg',
        label: 'български',
        anglicisedSuffix: '(Bulgarian)',
        iconPath: getIconPath('bg'),
    },
    {
        id: 'cs',
        label: 'čeština',
        anglicisedSuffix: '(Czech)',
        iconPath: getIconPath('cs'),
    },
    {
        id: 'da',
        label: 'Danske',
        anglicisedSuffix: '(Danish)',
        iconPath: getIconPath('da'),
    },
    {
        id: 'de',
        label: 'Deutsch',
        anglicisedSuffix: '(German)',
        iconPath: getIconPath('de'),
    },
    {
        id: 'el',
        label: 'ελληνικά',
        anglicisedSuffix: '(Greek)',
        iconPath: getIconPath('el'),
    },
    { id: 'en', label: 'English', iconPath: getIconPath('en') },
    { id: 'en-gb', label: 'English (UK)', iconPath: getIconPath('en-gb') },
    { id: 'en-us', label: 'English (US)', iconPath: getIconPath('en-us') },
    {
        id: 'es',
        label: 'Español',
        anglicisedSuffix: '(Spanish)',
        iconPath: getIconPath('es'),
    },
    {
        id: 'fa',
        label: 'فارسی',
        anglicisedSuffix: '(Persian)',
        iconPath: getIconPath('fa'),
    },
    {
        id: 'fi',
        label: 'Suomi',
        anglicisedSuffix: '(Finnish)',
        iconPath: getIconPath('fi'),
    },
    {
        id: 'fr',
        label: 'Français',
        anglicisedSuffix: '(French)',
        iconPath: getIconPath('fr'),
    },
    {
        id: 'hu',
        label: 'Magyar',
        anglicisedSuffix: '(Hungarian)',
        iconPath: getIconPath('hu'),
    },
    { id: 'id', label: 'Bahasa Indonesia', iconPath: getIconPath('id') },
    {
        id: 'it',
        label: 'Italiano',
        anglicisedSuffix: '(Italian)',
        iconPath: getIconPath('it'),
    },
    {
        id: 'iw',
        label: 'עִברִית',
        anglicisedSuffix: '(Hebrew)',
        iconPath: getIconPath('iw'),
    },
    {
        id: 'ja',
        label: '日本語',
        anglicisedSuffix: '(Japanese)',
        iconPath: getIconPath('ja'),
    },
    {
        id: 'ko',
        label: '한국어',
        anglicisedSuffix: '(Korean)',
        iconPath: getIconPath('ko'),
    },
    {
        id: 'nl',
        label: 'Nederlands',
        anglicisedSuffix: '(Dutch)',
        iconPath: getIconPath('nl'),
    },
    {
        id: 'nn',
        label: 'Norsk',
        anglicisedSuffix: '(Norwegian)',
        iconPath: getIconPath('nn'),
    },
    {
        id: 'pl',
        label: 'Polski',
        anglicisedSuffix: '(Polish)',
        iconPath: getIconPath('pl'),
    },
    {
        id: 'pt-br',
        label: 'Português/Brasil',
        anglicisedSuffix: '(Brazilian Portuguese)',
        iconPath: getIconPath('pt-br'),
    },
    {
        id: 'pt',
        label: 'Português',
        anglicisedSuffix: '(Portuguese)',
        iconPath: getIconPath('pt'),
    },
    {
        id: 'ro',
        label: 'Română',
        anglicisedSuffix: '(Romanian)',
        iconPath: getIconPath('ro'),
    },
    {
        id: 'ru',
        label: 'русский',
        anglicisedSuffix: '(Russian)',
        iconPath: getIconPath('ru'),
    },
    {
        id: 'sv',
        label: 'Svenska',
        anglicisedSuffix: '(Swedish)',
        iconPath: getIconPath('sv'),
    },
    {
        id: 'th',
        label: 'ภาษาไทย',
        anglicisedSuffix: '(Thai)',
        iconPath: getIconPath('th'),
    },
    {
        id: 'tr',
        label: 'Türk',
        anglicisedSuffix: '(Turkish)',
        iconPath: getIconPath('tr'),
    },
    {
        id: 'uk',
        label: 'український',
        anglicisedSuffix: '(Ukrainian)',
        iconPath: getIconPath('uk'),
    },
    {
        id: 'vi',
        label: 'Tiếng Việt',
        anglicisedSuffix: '(Vietnamese)',
        iconPath: getIconPath('vi'),
    },
    {
        id: 'zh',
        label: '简体中文',
        anglicisedSuffix: '(Simplified Chinese)',
        iconPath: getIconPath('zh'),
    },
];
function getSupportedLanguages({ showAnglicisedSuffix, }) {
    const windowLanguages = getSupoportedLanguagesFromWindow() || [];
    const supportedLanguages = windowLanguages.map(({ id }) => {
        const language = languages.find((lang) => lang.id === id);
        if (!language)
            throw new Error('useLanguages: Lanaguage not Mapped');
        const label = showAnglicisedSuffix && language.anglicisedSuffix
            ? `${language.label} ${language.anglicisedSuffix}`
            : language.label;
        const supportedLanguage = {
            id,
            iconPath: language.iconPath,
            label,
        };
        return supportedLanguage;
    });
    return supportedLanguages;
}
/**
 * Gets the path of a flag icon for a supported language
 */
function getIconPath(languageId) {
    return `https://cdn.elev.io/kb/assets/flags/${languageId}.svg`;
}
/**
 * # useLanguages
 */
export function useLanguages({ showAnglicisedSuffix } = {
    showAnglicisedSuffix: false,
}) {
    const supportedLanguages = getSupportedLanguages({ showAnglicisedSuffix });
    const selectedLanguageId = getLanguageId();
    const selectedLanguageIndex = supportedLanguages.findIndex((language) => selectedLanguageId === language.id);
    if (selectedLanguageIndex < 0)
        throw new Error('useLanguages > Invalid language selected');
    const selectedLanguage = supportedLanguages[selectedLanguageIndex];
    const setCurrentLanguageId = (languageId) => {
        if (languageId !== selectedLanguageId)
            changeLanguage(languageId);
    };
    return {
        supportedLanguages,
        selectedLanguage,
        setCurrentLanguageId,
    };
}
//# sourceMappingURL=useLanguages.js.map